import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ExecutionRunOption, GeneratedReport, ReportGeneratorConfig, SavedReport } from '../models/report-generator.model';

@Injectable({ providedIn: 'root' })
export class ReportGeneratorApiService {
    private baseUrl = `${environment.apiUrl}/report`;
    private executionResultsUrl = `${environment.apiUrl}/execution-results`;

    constructor(private http: HttpClient) {}

    getExecutionRuns(): Observable<ExecutionRunOption[]> {
        return this.http.get<ExecutionRunOption[]>(`${this.baseUrl}/execution-runs`);
    }

    getReportsByExecution(executionId: string): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/execution/${executionId}`);
    }

    getReportSummary(): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/summary`);
    }

    getStepsForReport(reportId: string): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/steps/${reportId}`);
    }

    getAllReports(): Observable<any[]> {
        return this.http.get<any[]>(this.baseUrl);
    }

    getExecutionResultsByRun(executionRunId: string): Observable<any[]> {
        return this.http.get<any[]>(`${this.executionResultsUrl}/run/${executionRunId}`);
    }

    generateReport(config: ReportGeneratorConfig): Observable<GeneratedReport> {
        return this.http.post<GeneratedReport>(`${this.baseUrl}/generate`, config);
    }

    getGeneratedReports(): Observable<GeneratedReport[]> {
        return this.http.get<GeneratedReport[]>(`${this.baseUrl}/generated`);
    }

    downloadReport(reportId: string, format: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/generated/${reportId}/download?format=${format}`, {
            responseType: 'blob'
        });
    }

    getSavedReports(): Observable<SavedReport[]> {
        return this.http.get<SavedReport[]>(`${this.baseUrl}/saved`);
    }

    saveReport(report: Partial<SavedReport>): Observable<SavedReport> {
        return this.http.post<SavedReport>(`${this.baseUrl}/saved`, report);
    }

    updateSavedReport(id: string, report: Partial<SavedReport>): Observable<SavedReport> {
        return this.http.patch<SavedReport>(`${this.baseUrl}/saved/${id}`, report);
    }

    deleteSavedReport(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/saved/${id}`);
    }

    getFilteredSummary(filters: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/filtered-summary`, filters);
    }

    streamReports(filters: any): Observable<string> {
        // Returns an Observable that emits NDJSON lines as they arrive
        return new Observable(subscriber => {
            const abortController = new AbortController();
            const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';

            fetch(`${this.baseUrl}/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(filters),
                signal: abortController.signal
            }).then(async response => {
                if (!response.ok) {
                    subscriber.error(new Error(`Stream failed: ${response.status}`));
                    return;
                }
                const reader = response.body!.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.trim()) {
                            subscriber.next(line);
                        }
                    }
                }

                if (buffer.trim()) {
                    subscriber.next(buffer);
                }
                subscriber.complete();
            }).catch(err => {
                if (err.name !== 'AbortError') {
                    subscriber.error(err);
                }
            });

            return () => abortController.abort();
        });
    }

    exportReportFromServer(filters: any, format: 'pdf' | 'excel'): Observable<Blob> {
        return this.http.post(`${this.baseUrl}/export`, { ...filters, format }, {
            responseType: 'blob'
        });
    }

}
